/**
 * Authentication Hook for QA AI Agent
 *
 * Manages authentication state and provides login/logout functionality
 */

import { useState, useEffect, useCallback } from 'react';
import authService from '../services/auth.service';
import { User, AuthState } from '../types';

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  /**
   * Check authentication status on mount
   */
  const checkAuth = useCallback(async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const { isAuthenticated, user } = await authService.checkAuth();

      setAuthState({
        isAuthenticated,
        isLoading: false,
        user,
        error: null,
      });

      return { isAuthenticated, user };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication check failed';
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: errorMessage,
      });

      return { isAuthenticated: false, user: null };
    }
  }, []);

  /**
   * Redirect to login page
   */
  const login = useCallback((returnUrl?: string) => {
    authService.redirectToLogin(returnUrl);
  }, []);

  /**
   * Logout and redirect
   */
  const logout = useCallback(async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));

    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    });
  }, []);

  /**
   * Initial authentication check
   */
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    ...authState,
    login,
    logout,
    checkAuth,
  };
};

export default useAuth;
